import { useEffect, useRef, useState } from "react"
import { motion } from "motion/react"
import { toast } from "sonner"
import {
  Zap,
  Mail01,
  MessageChatCircle,
  CheckCircle,
  SlashCircle01,
  Clock,
  Hand,
  Send01,
} from "@untitledui/icons"
import type {
  CustomerConsentState,
  CustomerEvent,
  CustomerEventKind,
} from "@/lib/api/customer-activity"
import { useSendCustomerMessage } from "@/lib/api/use-customer-activity"
import { cx } from "@/lib/utils/cx"

// The two-way conversation — every automated touch and every reply in one
// thread — plus a composer that does the writing for them (suggested one-tap
// replies, never a blank box). Reused on the Customer Card and, later, as the
// per-estimate lens on the estimate detail. When a customer isn't contactable
// (handled offline / opted out) the composer is closed, not just hidden — we
// never text a Do-Not-Contact customer.

// Canned openers so the advisor never faces an empty box. Tapping fills the
// composer (one tap); Send is the send — the outbound text keeps a human beat.
const SUGGESTED = [
  "Hi! Following up on your estimate — happy to get you scheduled. What day works?",
  "We can usually get you in this week. Is morning or afternoon better?",
  "Want me to hold your estimate while you decide? No rush.",
  "Great — I'll get you booked. What's the best number to reach you?",
]

// Offered once the repair is done — the natural moment to ask for a review (#28).
const REVIEW_OPENER =
  "Thanks for trusting us with the repair! If you're happy with how it turned out, a quick review would mean a lot to the team 🙏"

function relTime(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  // A future timestamp is a quiet-hours hold — say when it actually leaves.
  if (mins < 0)
    return `queued · ${new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })}`
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const h = Math.round(mins / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${d}d ago`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

// System (non-message) events render as a centered timeline row. `delivered`
// is dropped as noise; the signal events (opened/replied/scheduled/…) stay.
const SYSTEM: Partial<
  Record<CustomerEventKind, { icon: typeof Zap; label: string; tone: string }>
> = {
  enrolled: { icon: Zap, label: "Started automated follow-up", tone: "text-zinc-500" },
  sms_sent: { icon: MessageChatCircle, label: "Text sent", tone: "text-zinc-500" },
  email_sent: { icon: Mail01, label: "Email sent", tone: "text-zinc-500" },
  opened: { icon: Mail01, label: "Opened the email", tone: "text-emerald-600" },
  unresponsive: { icon: Clock, label: "No response yet", tone: "text-amber-600" },
  scheduled: { icon: CheckCircle, label: "Booked an appointment", tone: "text-emerald-600" },
  converted: { icon: CheckCircle, label: "Repair completed", tone: "text-emerald-600" },
  stopped: { icon: Hand, label: "Follow-up stopped — handled offline", tone: "text-zinc-500" },
  opted_out: { icon: SlashCircle01, label: "Opted out of messages", tone: "text-red-600" },
}

export function CustomerConversation({
  customerId,
  events,
  consent,
  estimateId,
  jobCompleted = false,
  customerName,
  composerRef,
  onSent,
  frameless = false,
}: {
  customerId: number
  events: CustomerEvent[]
  consent: CustomerConsentState
  estimateId?: number | null
  jobCompleted?: boolean
  customerName?: string
  /** The task screen's "Reply" button focuses the composer through this. */
  composerRef?: React.Ref<HTMLTextAreaElement>
  /** Fired after a message goes out — on the task screen, sending IS the answer. */
  onSent?: () => void
  /** Inside a pane that already has edges — no border, no rounding. */
  frameless?: boolean
}) {
  const [draft, setDraft] = useState("")
  const send = useSendCustomerMessage(customerId)
  const contactable = consent === "subscribed"
  const threadRef = useRef<HTMLDivElement>(null)
  const firstName = customerName?.split(" ")[0]

  // Per-estimate lens shows ONLY that estimate's events — customer-level
  // messages (estimateId null) belong to the full Customer Card, not here, so
  // one estimate's thread never bleeds into another's (F25).
  const shown = estimateId
    ? events.filter((e) => e.estimateId === estimateId)
    : events

  const suggestions = jobCompleted ? [REVIEW_OPENER, ...SUGGESTED] : SUGGESTED

  // The one thing this thread needs from you: is the customer's last message
  // still unanswered? (Only real messages count — system events don't.)
  const msgEvents = shown.filter(
    (e) => (e.kind === "replied" || e.kind === "agent_reply") && e.body,
  )
  const lastMsg = msgEvents[msgEvents.length - 1]
  const awaitingReply = contactable && lastMsg?.kind === "replied"

  // Keep the newest message in view — a thread that opens scrolled to the top
  // hides the reply you're responding to (F20).
  useEffect(() => {
    const el = threadRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [shown.length])

  const submit = () => {
    const body = draft.trim()
    if (!body || send.isPending) return
    send.mutate(
      { body, estimateId: estimateId ?? null },
      {
        onSuccess: () => {
          setDraft("")
          onSent?.()
        },
        // The draft stays in the box — nothing typed is ever lost (#52).
        onError: () => toast.error("Couldn't send — try again."),
      },
    )
  }

  return (
    <section>
      <div className={frameless ? "bg-white" : "rounded-2xl border border-zinc-200/80 bg-white overflow-hidden"}>
        {/* Thread */}
        <div
          ref={threadRef}
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label="Conversation history"
          className="px-4 sm:px-5 py-4 space-y-3 max-h-[26rem] overflow-y-auto"
        >
          {shown.length === 0 && (
            <p className="text-[13px] text-zinc-400 text-center py-6">
              No messages yet.
            </p>
          )}
          {shown.map((e) => {
            if (e.kind === "delivered") return null
            const isMessage = e.kind === "replied" || e.kind === "agent_reply"
            if (isMessage && e.body) {
              const outbound = e.direction === "outbound"
              return (
                <div
                  key={e.id}
                  className={cx("flex", outbound ? "justify-end" : "justify-start")}
                >
                  <div className="max-w-[80%]">
                    <div
                      className={cx(
                        "rounded-2xl px-3.5 py-2 text-[13.5px] leading-snug",
                        outbound
                          ? "bg-zinc-900 text-white rounded-br-md"
                          : "bg-zinc-100 text-zinc-800 rounded-bl-md",
                      )}
                    >
                      {e.body}
                    </div>
                    <p
                      className={cx(
                        "text-[10.5px] text-zinc-400 mt-1",
                        outbound ? "text-right" : "text-left",
                      )}
                    >
                      {/* STOP/START confirmations go out on their own — not "You". */}
                      {outbound
                        ? e.stepLabel === "Automatic"
                          ? "Automatic"
                          : "You"
                        : "Customer"}
                      {e.stepLabel === "Promotion" && " · promotion"}
                      {/* Which channel they answered on — text or email. */}
                      {!outbound && e.channel === "email" && " · email"}
                      {!outbound && e.channel === "sms" && " · text"} ·{" "}
                      {relTime(e.occurredAt)}
                    </p>
                  </div>
                </div>
              )
            }
            const cfg = SYSTEM[e.kind]
            if (!cfg) return null
            const Icon = cfg.icon
            return (
              <div key={e.id} className="flex items-center justify-center gap-2">
                <Icon className={cx("size-3.5 shrink-0", cfg.tone)} aria-hidden />
                <p className="text-[11.5px] text-zinc-500">
                  <span className={cfg.tone}>{cfg.label}</span>
                  {e.stepLabel && e.kind !== "enrolled" && (
                    <span className="text-zinc-400"> · {e.stepLabel}</span>
                  )}
                  <span className="text-zinc-300"> · {relTime(e.occurredAt)}</span>
                </p>
              </div>
            )
          })}
        </div>

        {/* Composer */}
        <div className="border-t border-zinc-100 bg-zinc-50/50 px-4 sm:px-5 py-3">
          {contactable ? (
            <>
              {/* The CTA — when the customer is waiting, say so plainly. */}
              {awaitingReply && (
                <div className="mb-2.5 rounded-lg bg-amber-50 border border-amber-200/70 px-3 py-2 text-[12.5px] font-medium text-amber-800">
                  {firstName ?? "The customer"} replied — waiting on your answer.
                </div>
              )}
              <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-400 mb-1.5">
                One-tap replies
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2.5">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setDraft(s)}
                    className="text-[11.5px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-2.5 py-1 hover:border-zinc-300 hover:bg-zinc-50 transition-colors max-w-full truncate"
                  >
                    {s.length > 42 ? `${s.slice(0, 42)}…` : s}
                  </button>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  ref={composerRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit()
                  }}
                  rows={2}
                  aria-label="Message to the customer"
                  placeholder={firstName ? `Reply to ${firstName}…` : "Text the customer…"}
                  className="flex-1 resize-none rounded-xl border border-zinc-200 bg-white px-3 py-2 text-[13.5px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300"
                />
                <motion.button
                  type="button"
                  onClick={submit}
                  disabled={!draft.trim() || send.isPending}
                  whileTap={{ scale: 0.96 }}
                  className="h-10 px-3.5 rounded-xl bg-zinc-900 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-black transition-colors"
                >
                  <Send01 className="size-4" aria-hidden />
                  Send
                </motion.button>
              </div>
              <p className="text-[10.5px] text-zinc-400 mt-1.5">
                Sends from your shop's number. ⌘↵ to send.
              </p>
            </>
          ) : (
            <div className="flex items-start gap-2.5 py-1">
              <SlashCircle01 className="size-4 text-zinc-400 shrink-0 mt-0.5" aria-hidden />
              <p className="text-[12.5px] text-zinc-500 leading-snug">
                Messaging is off for this customer{" "}
                {consent === "opted_out" ? "— they opted out" : "— marked handled offline"}.
                Resume follow-up above to text again.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
